import { PkButton, PkIcon, PkSimpleHeader } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { FC, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOptions, useTranslatedEntity } from '../../hooks';
import { useAppSelector } from '../../store';
import { getColorLightDark } from '../../utils/css-custom-props';
import GoogleTagManagerHelper from '../../utils/googleTagManager';
import { getLabel, getLink } from '../../utils/utils';

import * as style from './style.module.scss';

const ConditionalWrapper: FC<{
  condition: boolean;
  wrapper: (children: ReactNode) => JSX.Element;
  children: JSX.Element;
}> = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

export const validateLink: (link: string | undefined) => boolean = (link) => {
  if (!link) return false;
  if (link.toLowerCase().startsWith('http')) return true;
  return false;
};

const UpperBar = ({ mainHeader }: { mainHeader?: boolean }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { appName: title, backlinkText, backlinkUrl } = useOptions();
  const container = useRef<HTMLPkSimpleHeaderElement>(null);

  const goHome = () => {
    // remove hash from url
    location.hash = '/';
  };

  const sponsor = useAppSelector((state) => state.sponsors.sponsor);
  const currentPoll = useAppSelector((state) => state.polls.currentPoll);

  const { typeDescription } = useTranslatedEntity(currentPoll);

  const sponsorUrl = useMemo(() => getLink(sponsor, language), [language, sponsor]);
  const sponsorTag = useMemo(() => getLabel(sponsor, language), [language, sponsor]);

  const sponsorClick = () => {
    if (!sponsor) return null;
    GoogleTagManagerHelper.gtmSponsorClick({
      promotion_id: `${sponsor.code}-exclusive-section-header`,
      promotion_name: sponsor.code,
      creative_name: 'exclusive-section',
      creative_slot: 'header',
    });
  };

  return (
    <PkSimpleHeader
      ref={container}
      text={typeDescription ?? t(title)}
      bgColor='simple-header--bg-color'
      titleColor='simple-header--title-color'
      disableClose={mainHeader}
      contentMaxWidth={mainHeader ? 'var(--pk-grid--max-width)' : '100%'}
      height={mainHeader ? 78 : null}
      style={{
        '--pk-simple-header--bg-color': sponsor?.color,
        '--pk-simple-header--title-color': sponsor?.textColor,
      }}
      className={clsx(
        style.gotxUpperBar,
        mainHeader && style.mainHeader,
        sponsor && `pk-theme--${getColorLightDark(sponsor.color)}`,
      )}
      onPkClose={goHome}>
      {mainHeader && !!backlinkUrl && !!backlinkText && (
        <PkButton slot='title-prefix' appearance='text' linkHref={backlinkUrl.replace('[LANG]', language)}>
          <PkIcon name='navigation-chevron-left' className='pk-text--text-02' size={16} title={t(backlinkText)} />
          <span className='pk-font-size--xs pk-text--text-02'>{t(backlinkText)}</span>
        </PkButton>
      )}
      {sponsor && (
        <div slot='sponsor' className={style.gotxUpperBarSponsor}>
          {!!sponsorTag && <p className={style.gotxUpperBarLabel}>{sponsorTag}</p>}
          <div className={style.gotxUpperBarLogo}>
            <ConditionalWrapper
              condition={validateLink(sponsorUrl)}
              wrapper={(children) => (
                <a title={sponsor.name} onClick={sponsorClick} href={sponsorUrl}>
                  {children}
                </a>
              )}>
              <img className={style.gotxUpperBarImg} loading='lazy' alt={sponsor.title} src={sponsor.image} />
            </ConditionalWrapper>
          </div>
        </div>
      )}
    </PkSimpleHeader>
  );
};

export default UpperBar;
